// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import ClassStep from "./class-step.js"
import Contact from "./contact.js"
import Email from "./email.js"
import Organization from "./organization.js"
import Person from "./person.js"
import Role from "./role.js"
import SchoolYear from "./school-year.js"
import Survey from "./survey.js"
import TeamSurvey from "./team-survey.js"
import TeamUser from "./team-user.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "deleted_at": {
      "column": {
        "default": null,
        "name": "deleted_at",
        "null": true,
        "type": "datetime"
      },
      "name": "deleted_at",
      "selected_by_default": null,
      "translated": null
    },
    "email": {
      "column": null,
      "name": "email",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "is_admin": {
      "column": null,
      "name": "is_admin",
      "selected_by_default": null,
      "translated": null
    },
    "is_student": {
      "column": null,
      "name": "is_student",
      "selected_by_default": null,
      "translated": null
    },
    "is_super_admin": {
      "column": null,
      "name": "is_super_admin",
      "selected_by_default": null,
      "translated": null
    },
    "is_teacher": {
      "column": null,
      "name": "is_teacher",
      "selected_by_default": null,
      "translated": null
    },
    "microsoft_identifier": {
      "column": {
        "default": null,
        "name": "microsoft_identifier",
        "null": true,
        "type": "string"
      },
      "name": "microsoft_identifier",
      "selected_by_default": null,
      "translated": null
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": null,
      "translated": null
    },
    "otp_delivery_method": {
      "column": {
        "default": "email",
        "name": "otp_delivery_method",
        "null": false,
        "type": "string"
      },
      "name": "otp_delivery_method",
      "selected_by_default": null,
      "translated": null
    },
    "otp_required_for_login": {
      "column": {
        "default": false,
        "name": "otp_required_for_login",
        "null": false,
        "type": "boolean"
      },
      "name": "otp_required_for_login",
      "selected_by_default": null,
      "translated": null
    },
    "otp_secret_present": {
      "column": null,
      "name": "otp_secret_present",
      "selected_by_default": null,
      "translated": null
    },
    "person_id": {
      "column": {
        "default": null,
        "name": "person_id",
        "null": true,
        "type": "uuid"
      },
      "name": "person_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "uni_consent_ip": {
      "column": {
        "default": null,
        "name": "uni_consent_ip",
        "null": true,
        "type": "string"
      },
      "name": "uni_consent_ip",
      "selected_by_default": null,
      "translated": null
    },
    "team_surveys_count": {
      "column": null,
      "name": "team_surveys_count",
      "selected_by_default": false,
      "translated": null
    }
  },
  "className": "User",
  "collectionKey": "users",
  "collectionName": "users",
  "i18nKey": "user",
  "camelizedLower": "user",
  "name": "User",
  "nameDasherized": "user",
  "pluralName": "users",
  "ransackable_associations": [
    {
      "className": "Ability",
      "collectionName": "abilities",
      "foreignKey": "ability_id",
      "name": "abilities",
      "macro": "has_many",
      "resource_name": "Ability",
      "through": "person"
    },
    {
      "className": "ActivityParticipant",
      "collectionName": "activity_participants",
      "foreignKey": "approved_by_id",
      "name": "approved_activity_participants",
      "macro": "has_many",
      "resource_name": "ActivityParticipant",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "user_id",
      "name": "author_on_logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "has_one",
      "resource_name": "ClassStep",
      "through": "current_school_year"
    },
    {
      "className": "Comment",
      "collectionName": "comments",
      "foreignKey": "user_id",
      "name": "comments",
      "macro": "has_many",
      "resource_name": "Comment",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "person_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "Message",
      "collectionName": "messages",
      "foreignKey": "creator_id",
      "name": "created_messages",
      "macro": "has_many",
      "resource_name": "Message",
      "through": null
    },
    {
      "className": "Reimbursement",
      "collectionName": "reimbursements",
      "foreignKey": "creator_id",
      "name": "created_reimbursements",
      "macro": "has_many",
      "resource_name": "Reimbursement",
      "through": null
    },
    {
      "className": "SchoolYear",
      "collectionName": "school_years",
      "foreignKey": "contact_id",
      "name": "current_school_year",
      "macro": "has_one",
      "resource_name": "SchoolYear",
      "through": "contact"
    },
    {
      "className": "WorkerPlugins::Workplace",
      "collectionName": "workplaces",
      "foreignKey": "current_workplace_id",
      "name": "current_workplace",
      "macro": "belongs_to",
      "resource_name": "Workplace",
      "through": null
    },
    {
      "className": "Email",
      "collectionName": "emails",
      "foreignKey": "contact_id",
      "name": "emails",
      "macro": "has_many",
      "resource_name": "Email",
      "through": "contact"
    },
    {
      "className": "ActivityParticipant",
      "collectionName": "activity_participants",
      "foreignKey": "enrolled_by_id",
      "name": "enrolled_activity_participants",
      "macro": "has_many",
      "resource_name": "ActivityParticipant",
      "through": null
    },
    {
      "className": "HighscoreExcludedUser",
      "collectionName": "highscore_excluded_users",
      "foreignKey": "excluded_user_id",
      "name": "highscore_excluded_users",
      "macro": "has_many",
      "resource_name": "HighscoreExcludedUser",
      "through": null
    },
    {
      "className": "Jobler::Job",
      "collectionName": "jobler_jobs",
      "foreignKey": "user_id",
      "name": "jobler_jobs",
      "macro": "has_many",
      "resource_name": "JoblerJob",
      "through": null
    },
    {
      "className": "LockedField",
      "collectionName": "locked_fields",
      "foreignKey": "locked_by_id",
      "name": "locked_fields",
      "macro": "has_many",
      "resource_name": "LockedField",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "ModelSearch",
      "collectionName": "model_searches",
      "foreignKey": "user_id",
      "name": "model_searches",
      "macro": "has_many",
      "resource_name": "ModelSearch",
      "through": null
    },
    {
      "className": "OrganizationUserRelationship",
      "collectionName": null,
      "foreignKey": "user_id",
      "name": "organization_user_relationships",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    },
    {
      "className": "TeamUser",
      "collectionName": "team_users",
      "foreignKey": "user_id",
      "name": "owner_on_team_users",
      "macro": "has_many",
      "resource_name": "TeamUser",
      "through": null
    },
    {
      "className": "Person",
      "collectionName": "people",
      "foreignKey": "person_id",
      "name": "person",
      "macro": "belongs_to",
      "resource_name": "Person",
      "through": null
    },
    {
      "className": "PersonRole",
      "collectionName": "person_roles",
      "foreignKey": "person_id",
      "name": "person_roles",
      "macro": "has_many",
      "resource_name": "PersonRole",
      "through": "person"
    },
    {
      "className": "Email",
      "collectionName": "emails",
      "foreignKey": "contact_id",
      "name": "primary_email",
      "macro": "has_one",
      "resource_name": "Email",
      "through": "contact"
    },
    {
      "className": "PhoneNumber",
      "collectionName": "phone_numbers",
      "foreignKey": "contact_id",
      "name": "primary_phone_number",
      "macro": "has_one",
      "resource_name": "PhoneNumber",
      "through": "contact"
    },
    {
      "className": "ActivityParticipant",
      "collectionName": "activity_participants",
      "foreignKey": "registered_by_id",
      "name": "registered_activity_participants",
      "macro": "has_many",
      "resource_name": "ActivityParticipant",
      "through": null
    },
    {
      "className": "ReimbursementAttachment",
      "collectionName": "reimbursement_attachments",
      "foreignKey": "user_id",
      "name": "reimbursement_attachments",
      "macro": "has_many",
      "resource_name": "ReimbursementAttachment",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "roles",
      "macro": "has_many",
      "resource_name": "Role",
      "through": "person"
    },
    {
      "className": "Organization",
      "collectionName": "organizations",
      "foreignKey": "school_id",
      "name": "school",
      "macro": "has_one",
      "resource_name": "Organization",
      "through": "current_school_year"
    },
    {
      "className": "SchoolClass",
      "collectionName": "school_classes",
      "foreignKey": "school_class_id",
      "name": "school_class",
      "macro": "has_one",
      "resource_name": "SchoolClass",
      "through": "current_school_year"
    },
    {
      "className": "SubscriptionRenewalGeneration",
      "collectionName": "subscription_renewal_generations",
      "foreignKey": "user_id",
      "name": "subscription_renewal_generations",
      "macro": "has_many",
      "resource_name": "SubscriptionRenewalGeneration",
      "through": null
    },
    {
      "className": "Survey",
      "collectionName": "surveys",
      "foreignKey": "survey_id",
      "name": "surveys",
      "macro": "has_many",
      "resource_name": "Survey",
      "through": "team_surveys"
    },
    {
      "className": "TableSetting",
      "collectionName": "table_settings",
      "foreignKey": "user_id",
      "name": "table_settings",
      "macro": "has_many",
      "resource_name": "TableSetting",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "TeamApplication",
      "collectionName": "team_applications",
      "foreignKey": "user_id",
      "name": "team_applications",
      "macro": "has_many",
      "resource_name": "TeamApplication",
      "through": null
    },
    {
      "className": "TeamSurvey",
      "collectionName": "team_surveys",
      "foreignKey": "team_id",
      "name": "team_surveys",
      "macro": "has_many",
      "resource_name": "TeamSurvey",
      "through": "teams"
    },
    {
      "className": "TeamUser",
      "collectionName": "team_users",
      "foreignKey": "user_id",
      "name": "team_users",
      "macro": "has_many",
      "resource_name": "TeamUser",
      "through": null
    },
    {
      "className": "Team",
      "collectionName": "teams",
      "foreignKey": "team_id",
      "name": "teams",
      "macro": "has_many",
      "resource_name": "Team",
      "through": "team_users"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "ActivityParticipant",
      "collectionName": "activity_participants",
      "foreignKey": "unregistered_by_id",
      "name": "unregistered_activity_participants",
      "macro": "has_many",
      "resource_name": "ActivityParticipant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "consumed_timestep",
      "column": {
        "default": null,
        "name": "consumed_timestep",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "current_workplace_id",
      "column": {
        "default": null,
        "name": "current_workplace_id",
        "null": true,
        "type": "uuid"
      }
    },
    {
      "name": "deleted_at",
      "column": {
        "default": null,
        "name": "deleted_at",
        "null": true,
        "type": "datetime"
      }
    },
    {
      "name": "deprecated_email",
      "column": {
        "default": "",
        "name": "deprecated_email",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "encrypted_otp_secret",
      "column": {
        "default": null,
        "name": "encrypted_otp_secret",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "encrypted_otp_secret_iv",
      "column": {
        "default": null,
        "name": "encrypted_otp_secret_iv",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "encrypted_otp_secret_salt",
      "column": {
        "default": null,
        "name": "encrypted_otp_secret_salt",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "encrypted_password",
      "column": {
        "default": "",
        "name": "encrypted_password",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "microsoft_identifier",
      "column": {
        "default": null,
        "name": "microsoft_identifier",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "old_current_workplace_id",
      "column": {
        "default": null,
        "name": "old_current_workplace_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_person_id",
      "column": {
        "default": null,
        "name": "old_person_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_role",
      "column": {
        "default": "student",
        "name": "old_role",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "otp_delivery_method",
      "column": {
        "default": "email",
        "name": "otp_delivery_method",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "otp_required_for_login",
      "column": {
        "default": false,
        "name": "otp_required_for_login",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "otp_secret",
      "column": {
        "default": null,
        "name": "otp_secret",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "person_id",
      "column": {
        "default": null,
        "name": "person_id",
        "null": true,
        "type": "uuid"
      }
    },
    {
      "name": "remember_created_at",
      "column": {
        "default": null,
        "name": "remember_created_at",
        "null": true,
        "type": "datetime"
      }
    },
    {
      "name": "reset_password_sent_at",
      "column": {
        "default": null,
        "name": "reset_password_sent_at",
        "null": true,
        "type": "datetime"
      }
    },
    {
      "name": "reset_password_token",
      "column": {
        "default": null,
        "name": "reset_password_token",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "uni_consent_ip",
      "column": {
        "default": null,
        "name": "uni_consent_ip",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "nemoa_select_search"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "ClassStep",
      "collectionName": "class_steps",
      "foreignKey": "class_step_id",
      "name": "class_step",
      "macro": "has_one",
      "resource_name": "ClassStep",
      "through": "current_school_year"
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "person_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "SchoolYear",
      "collectionName": "school_years",
      "foreignKey": "contact_id",
      "name": "current_school_year",
      "macro": "has_one",
      "resource_name": "SchoolYear",
      "through": "contact"
    },
    {
      "className": "Email",
      "collectionName": "emails",
      "foreignKey": "contact_id",
      "name": "emails",
      "macro": "has_many",
      "resource_name": "Email",
      "through": "contact"
    },
    {
      "className": "Person",
      "collectionName": "people",
      "foreignKey": "person_id",
      "name": "person",
      "macro": "belongs_to",
      "resource_name": "Person",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "roles",
      "macro": "has_many",
      "resource_name": "Role",
      "through": "person"
    },
    {
      "className": "Organization",
      "collectionName": "organizations",
      "foreignKey": "school_id",
      "name": "school",
      "macro": "has_one",
      "resource_name": "Organization",
      "through": "current_school_year"
    },
    {
      "className": "Survey",
      "collectionName": "surveys",
      "foreignKey": "survey_id",
      "name": "surveys",
      "macro": "has_many",
      "resource_name": "Survey",
      "through": "team_surveys"
    },
    {
      "className": "TeamSurvey",
      "collectionName": "team_surveys",
      "foreignKey": "team_id",
      "name": "team_surveys",
      "macro": "has_many",
      "resource_name": "TeamSurvey",
      "through": "teams"
    },
    {
      "className": "TeamUser",
      "collectionName": "team_users",
      "foreignKey": "user_id",
      "name": "team_users",
      "macro": "has_many",
      "resource_name": "TeamUser",
      "through": null
    }
  ],
  "paramKey": "user",
  "primaryKey": "id"
}

/** Frontend model for User. */
export default class User extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {Date | null} */
  deletedAt() {
    return this.readAttributeUnderscore("deleted_at")
  }

  /** @returns {boolean} */
  hasDeletedAt() {
    const value = this.deletedAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  email() {
    return this.readAttributeUnderscore("email")
  }

  /** @returns {boolean} */
  hasEmail() {
    const value = this.email()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  isAdmin() {
    return this.readAttributeUnderscore("is_admin")
  }

  /** @returns {boolean} */
  hasIsAdmin() {
    const value = this.isAdmin()

    return this._isPresent(value)
  }

  /** @returns {any} */
  isStudent() {
    return this.readAttributeUnderscore("is_student")
  }

  /** @returns {boolean} */
  hasIsStudent() {
    const value = this.isStudent()

    return this._isPresent(value)
  }

  /** @returns {any} */
  isSuperAdmin() {
    return this.readAttributeUnderscore("is_super_admin")
  }

  /** @returns {boolean} */
  hasIsSuperAdmin() {
    const value = this.isSuperAdmin()

    return this._isPresent(value)
  }

  /** @returns {any} */
  isTeacher() {
    return this.readAttributeUnderscore("is_teacher")
  }

  /** @returns {boolean} */
  hasIsTeacher() {
    const value = this.isTeacher()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  microsoftIdentifier() {
    return this.readAttributeUnderscore("microsoft_identifier")
  }

  /** @returns {boolean} */
  hasMicrosoftIdentifier() {
    const value = this.microsoftIdentifier()

    return this._isPresent(value)
  }

  /** @returns {any} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {string} */
  otpDeliveryMethod() {
    return this.readAttributeUnderscore("otp_delivery_method")
  }

  /** @returns {boolean} */
  hasOtpDeliveryMethod() {
    const value = this.otpDeliveryMethod()

    return this._isPresent(value)
  }

  /** @returns {boolean} */
  otpRequiredForLogin() {
    return this.readAttributeUnderscore("otp_required_for_login")
  }

  /** @returns {boolean} */
  hasOtpRequiredForLogin() {
    const value = this.otpRequiredForLogin()

    return this._isPresent(value)
  }

  /** @returns {any} */
  otpSecretPresent() {
    return this.readAttributeUnderscore("otp_secret_present")
  }

  /** @returns {boolean} */
  hasOtpSecretPresent() {
    const value = this.otpSecretPresent()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  personId() {
    return this.readAttributeUnderscore("person_id")
  }

  /** @returns {boolean} */
  hasPersonId() {
    const value = this.personId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  uniConsentIp() {
    return this.readAttributeUnderscore("uni_consent_ip")
  }

  /** @returns {boolean} */
  hasUniConsentIp() {
    const value = this.uniConsentIp()

    return this._isPresent(value)
  }

  /** @returns {any} */
  teamSurveysCount() {
    return this.readAttributeUnderscore("team_surveys_count")
  }

  /** @returns {boolean} */
  hasTeamSurveysCount() {
    const value = this.teamSurveysCount()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static applyForgottenPasswordRequest(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "apply_forgotten_password_request",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static authenticateUserFromUniAuth(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "authenticate_user_from_uni_auth",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static currenciesCollection(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "currencies_collection",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static destroyAttachment(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "destroy_attachment",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static newForgottenPasswordRequest(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "new_forgotten_password_request",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static storeLocation(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "store_location",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static registrate(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "registrate",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static resendConfirmationEmail(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "resend_confirmation_email",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static sendOtp(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "send_otp",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static signIn(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "sign_in",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static signInAnonymously(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "sign_in_anonymously",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static signOut(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "sign_out",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static softDeleteModel(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "soft_delete_model",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static stateCall(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "state_call",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static unSoftDeleteModel(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "un_soft_delete_model",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static uniFakeLogin(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "uni_fake_login",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static uniSsoGenerateLoginUrl(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "uni_sso_generate_login_url",
        collectionName: "users",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  consentToTerms(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "consent_to_terms",
        primaryKey: this.primaryKey(),
        collectionName: "users",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  disableTwoFactorAuthentication(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "disable_two_factor_authentication",
        primaryKey: this.primaryKey(),
        collectionName: "users",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  enableTwoFactorAuthentication(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "enable_two_factor_authentication",
        primaryKey: this.primaryKey(),
        collectionName: "users",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  missingRequiredAttributes(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "missing_required_attributes",
        primaryKey: this.primaryKey(),
        collectionName: "users",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  signInAs(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "sign_in_as",
        primaryKey: this.primaryKey(),
        collectionName: "users",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  syncMicrosoftIdentifier(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "sync_microsoft_identifier",
        primaryKey: this.primaryKey(),
        collectionName: "users",
        type: "member"
      },
      commandArgs
    ))
  }

  /** @returns {import("./class-step.js").default | null} */
  classStep() {
    return this._readHasOneReflection({modelClass: ClassStep, reflectionName: "class_step"})
  }

  /** @returns {Promise<import("./class-step.js").default | null>} */
  loadClassStep() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()

    return this._loadHasOneReflection(
      {
        reflectionName: "class_step",
        model: this,
        modelClass: ClassStep
      },
      {
        params: {
          through: {
            model: "User",
            id,
            reflection: "class_step"
          }
        }
      }
    )
  }

  /** @returns {Promise<import("./class-step.js").default | null>} */
  ensureClassStepLoaded() {
    return this.ensureAssociationLoaded("class_step")
  }

  /** @returns {import("./contact.js").default | null} */
  contact() {
    return this._readBelongsToReflection({modelClass: Contact, reflectionName: "contact"})
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  loadContact() {
    if (!("personId" in this)) throw new Error("Foreign key method wasn't defined: personId")

    const id = this.personId()
    const ransack = {}

    ransack["person_id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "contact",
        model: this,
        modelClass: Contact
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  ensureContactLoaded() {
    return this.ensureAssociationLoaded("contact")
  }

  /** @returns {import("./school-year.js").default | null} */
  currentSchoolYear() {
    return this._readHasOneReflection({modelClass: SchoolYear, reflectionName: "current_school_year"})
  }

  /** @returns {Promise<import("./school-year.js").default | null>} */
  loadCurrentSchoolYear() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()

    return this._loadHasOneReflection(
      {
        reflectionName: "current_school_year",
        model: this,
        modelClass: SchoolYear
      },
      {
        params: {
          through: {
            model: "User",
            id,
            reflection: "current_school_year"
          }
        }
      }
    )
  }

  /** @returns {Promise<import("./school-year.js").default | null>} */
  ensureCurrentSchoolYearLoaded() {
    return this.ensureAssociationLoaded("current_school_year")
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./email.js").default>} */
  emails() {
    return new Collection(
      {
        reflectionName: "emails",
        model: this,
        modelClass: Email
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "emails"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./email.js").default>>} */
  loadEmails() {
    return this._loadHasManyReflection(
      {
        reflectionName: "emails",
        model: this,
        modelClass: Email
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "emails"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./email.js").default>>} */
  ensureEmailsLoaded() {
    return this.ensureAssociationLoaded("emails")
  }

  /** @returns {import("./person.js").default | null} */
  person() {
    return this._readBelongsToReflection({modelClass: Person, reflectionName: "person"})
  }

  /** @returns {Promise<import("./person.js").default | null>} */
  loadPerson() {
    if (!("personId" in this)) throw new Error("Foreign key method wasn't defined: personId")

    const id = this.personId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "person",
        model: this,
        modelClass: Person
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./person.js").default | null>} */
  ensurePersonLoaded() {
    return this.ensureAssociationLoaded("person")
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./role.js").default>} */
  roles() {
    return new Collection(
      {
        reflectionName: "roles",
        model: this,
        modelClass: Role
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "roles"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./role.js").default>>} */
  loadRoles() {
    return this._loadHasManyReflection(
      {
        reflectionName: "roles",
        model: this,
        modelClass: Role
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "roles"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./role.js").default>>} */
  ensureRolesLoaded() {
    return this.ensureAssociationLoaded("roles")
  }

  /** @returns {import("./organization.js").default | null} */
  school() {
    return this._readHasOneReflection({modelClass: Organization, reflectionName: "school"})
  }

  /** @returns {Promise<import("./organization.js").default | null>} */
  loadSchool() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()

    return this._loadHasOneReflection(
      {
        reflectionName: "school",
        model: this,
        modelClass: Organization
      },
      {
        params: {
          through: {
            model: "User",
            id,
            reflection: "school"
          }
        }
      }
    )
  }

  /** @returns {Promise<import("./organization.js").default | null>} */
  ensureSchoolLoaded() {
    return this.ensureAssociationLoaded("school")
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./survey.js").default>} */
  surveys() {
    return new Collection(
      {
        reflectionName: "surveys",
        model: this,
        modelClass: Survey
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "surveys"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./survey.js").default>>} */
  loadSurveys() {
    return this._loadHasManyReflection(
      {
        reflectionName: "surveys",
        model: this,
        modelClass: Survey
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "surveys"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./survey.js").default>>} */
  ensureSurveysLoaded() {
    return this.ensureAssociationLoaded("surveys")
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./team-survey.js").default>} */
  teamSurveys() {
    return new Collection(
      {
        reflectionName: "team_surveys",
        model: this,
        modelClass: TeamSurvey
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "team_surveys"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./team-survey.js").default>>} */
  loadTeamSurveys() {
    return this._loadHasManyReflection(
      {
        reflectionName: "team_surveys",
        model: this,
        modelClass: TeamSurvey
      },
      {
        params: {
          through: {
            model: "User",
            id: this.primaryKey(),
            reflection: "team_surveys"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./team-survey.js").default>>} */
  ensureTeamSurveysLoaded() {
    return this.ensureAssociationLoaded("team_surveys")
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./team-user.js").default>} */
  teamUsers() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["user_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "team_users",
        model: this,
        modelClass: TeamUser
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./team-user.js").default>>} */
  loadTeamUsers() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["user_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "team_users",
        model: this,
        modelClass: TeamUser
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./team-user.js").default>>} */
  ensureTeamUsersLoaded() {
    return this.ensureAssociationLoaded("team_users")
  }
}
